import { useCallback, useMemo, useState } from "react";

import type { EntityRecordResponse } from "@btnet/services-btnet/handlers/models/entityRecordResponse";
import type { EntityType } from "@btnet/services-btnet/handlers/models/entityType";
import { cn } from "@btnet/utilities-core";

import { BdsButton } from "@buildertrend/components";

import { EntityLinkRoutes } from "commonComponents/entity/entityLinkSelector/EntityLinkRoutes";
import { RelatedItemTag } from "commonComponents/entity/relatedItem/RelatedItemTag/RelatedItemTag";

import "./EntityLinkSelectorReadOnly.less";

import { type EntityStatusRenderer } from "./EntityLinkSelector.types";
import {
    createRelatedItemTagPropsFromEntities,
    updateLinkedEntitiesCache,
} from "./EntityLinkSelector.utilities";
import { useOverflowDetection } from "./hooks/useOverflowDetection";

interface IEntityLinkSelectorReadOnlyProps {
    /**
     * Raw linked entities data from API
     */
    linkedEntities: EntityRecordResponse[];

    /**
     * Target entity type being linked
     */
    targetEntityType: EntityType;

    /**
     * Job ID for routing
     */
    jobId: number;

    /**
     * Field path for test IDs
     */
    fieldPath: string;

    /**
     * Display name plural (e.g., "specifications")
     */
    entityNamePlural: string;

    /**
     * Source external ID for cache invalidation when entities are deleted
     */
    sourceExternalId: string;

    /**
     * Entity types used to construct the React Query cache key for the batch linked-entities request.
     * Must match the array used by the parent EntityLinkSelector so cache updates target the correct key.
     */
    queryKeyEntityTypes: EntityType[];

    /**
     * Optional custom render function for status badges
     * Use this to add entity-specific status displays (e.g., Draft/Published badges)
     */
    renderStatus?: EntityStatusRenderer;

    // Note: canRemove is currently hardcoded to false. Can add as prop in future if needed.
}

/**
 * Read-only display component for linked entities
 * Handles overflow detection and "Show All/Show Less" functionality
 */
export const EntityLinkSelectorReadOnly: React.FunctionComponent<
    IEntityLinkSelectorReadOnlyProps
> = ({
    linkedEntities,
    targetEntityType,
    jobId,
    fieldPath,
    entityNamePlural,
    sourceExternalId,
    queryKeyEntityTypes,
    renderStatus,
}) => {
    const [showAllTags, setShowAllTags] = useState(false);

    // Not currently needed since canRemove is hardcoded to false
    const handleRemove = useCallback((_externalId: string) => {}, []);

    const linkedEntityTags = useMemo(
        () =>
            createRelatedItemTagPropsFromEntities(
                linkedEntities,
                targetEntityType,
                jobId,
                handleRemove,
                false,
                renderStatus,
            ),
        [linkedEntities, targetEntityType, jobId, handleRemove, renderStatus],
    );

    // Overflow detection hook only runs in this component when read-only mode is active
    const { containerRef, hasOverflow } = useOverflowDetection({
        enabled: !showAllTags && linkedEntityTags.length > 0,
        itemCount: linkedEntityTags.length,
    });

    // Update the linked entities cache when an entity is deleted from the modal
    const handleDeleted = useCallback(
        (externalId: string) => {
            const filtered = linkedEntities.filter((e) => e.id !== externalId);
            updateLinkedEntitiesCache(
                sourceExternalId,
                targetEntityType,
                filtered,
                queryKeyEntityTypes,
            );
        },
        [sourceExternalId, targetEntityType, linkedEntities, queryKeyEntityTypes],
    );

    if (linkedEntityTags.length === 0) {
        return <div>No {entityNamePlural} linked</div>;
    }

    return (
        <div className="entity-link-selector-readonly">
            <EntityLinkRoutes onDeleted={handleDeleted} onEdited={() => {}} />

            <div
                ref={containerRef}
                className={cn("tag-container", {
                    "tag-container--expanded": showAllTags,
                    "tag-container--collapsed": !showAllTags,
                })}
            >
                {linkedEntityTags.map((tagProps, index) => (
                    <RelatedItemTag
                        {...tagProps}
                        key={tagProps.externalId}
                        data-testid={`${fieldPath}-tag-${index}`}
                        hideEntityTypeLabel
                        noWrapping
                        canRemove={false}
                        useMultiTagMargins
                    />
                ))}
            </div>
            {hasOverflow && !showAllTags && (
                <div className="button-wrapper">
                    <BdsButton
                        testid={`show-all-${fieldPath}-button`}
                        displayType="tertiary"
                        text={`Show all (${linkedEntityTags.length})`}
                        onClick={() => setShowAllTags(true)}
                    />
                </div>
            )}
            {showAllTags && (
                <div className="button-wrapper">
                    <BdsButton
                        testid={`show-less-${fieldPath}-button`}
                        displayType="tertiary"
                        text="Show less"
                        onClick={() => setShowAllTags(false)}
                    />
                </div>
            )}
        </div>
    );
};
